/**
 * Placement rule for the node toolbar (the editing card shown above a selected node).
 *
 * The toolbar renders inside the reactflow pane, so when the node sits close to the top of the
 * canvas a toolbar placed above it overflows the pane and gets clipped. In that case it must flip
 * below the node so it stays fully visible.
 */

// Estimated rendered height of the toolbar card in px (code + name fields stacked, rework
// checkbox and the action icons). The toolbar flips below the node when there is less room than
// this above it.
export const NODE_TOOLBAR_ESTIMATED_HEIGHT = 260

/**
 * Resolves on which side of the node the toolbar should render.
 * Returned values match reactflow's Position enum ('top' | 'bottom').
 *
 * @param {number} nodeScreenY Node Y coordinate in screen/pane space (already pan/zoom adjusted)
 * @returns {'top'|'bottom'} 'bottom' when there is not enough room above the node
 */
export const resolveNodeToolbarPosition = (nodeScreenY) =>
  nodeScreenY < NODE_TOOLBAR_ESTIMATED_HEIGHT ? 'bottom' : 'top'

/**
 * Narrows the pane rect by overlay elements docked over its side edges (e.g. the technologies
 * palette and its open/close button, absolutely positioned over the flow's left edge), returning
 * the horizontal bounds actually visible for the toolbar.
 *
 * @param {{left: number, right: number}} paneRect Pane bounding rect in viewport space
 * @param {Array<{left: number, right: number}>} obstacleRects Bounding rects of overlays inside the pane area
 * @returns {{left: number, right: number}} Visible horizontal bounds
 */
export const resolveVisibleBounds = (paneRect, obstacleRects) => {
  let left = paneRect.left
  let right = paneRect.right
  for (const obstacle of obstacleRects) {
    if (obstacle.right <= obstacle.left) {
      continue
    }
    if (obstacle.left <= left && obstacle.right > left && obstacle.right < right) {
      left = obstacle.right
    }
    if (obstacle.right >= right && obstacle.left < right && obstacle.left > left) {
      right = obstacle.left
    }
  }
  return { left, right }
}

/**
 * Resolves the horizontal shift (px) needed to keep the toolbar fully inside the visible bounds,
 * from the MEASURED bounding rects of the rendered toolbar and of the pane. Unlike an
 * estimate-based alignment, this stays exact for any zoom level, toolbar size or node partially
 * outside the canvas. When the toolbar is wider than the bounds, its left side wins (the fields
 * start there).
 *
 * @param {{left: number, right: number}} toolbarRect Toolbar bounding rect in viewport space
 * @param {{left: number, right: number}} paneRect Visible bounds in viewport space
 * @returns {number} Positive to push right, negative to push left, 0 when already fully visible
 */
export const resolveToolbarHorizontalShift = (toolbarRect, paneRect) => {
  if (toolbarRect.left < paneRect.left) {
    return paneRect.left - toolbarRect.left
  }
  if (toolbarRect.right > paneRect.right) {
    return paneRect.right - toolbarRect.right
  }
  return 0
}
